import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Mail, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface WaitlistSignup {
  id: string;
  email: string;
  name: string;
  notes: string | null;
  created_at: string;
}

const WaitlistManagement = () => {
  const [signups, setSignups] = useState<WaitlistSignup[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSignups();
  }, []);

  const fetchSignups = async () => {
    try {
      const { data, error } = await supabase
        .from('waitlist_signups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSignups(data || []);
    } catch (error) {
      console.error('Error fetching waitlist signups:', error);
      toast.error('Failed to load waitlist signups');
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Notes', 'Signup Date'];
    const rows = signups.map(signup => [
      signup.name,
      signup.email,
      signup.notes || '',
      format(new Date(signup.created_at), 'PPpp')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist-signups-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Waitlist exported successfully');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Waitlist Signups</CardTitle>
            <CardDescription>
              {signups.length} total signups
            </CardDescription>
          </div>
          <Button onClick={exportToCSV} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {signups.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No waitlist signups yet
            </p>
          ) : (
            signups.map((signup) => (
              <div
                key={signup.id}
                className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{signup.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{signup.email}</span>
                  </div>
                  {signup.notes && (
                    <p className="text-sm text-muted-foreground italic">
                      "{signup.notes}"
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2 md:mt-0">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(signup.created_at), 'PPp')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default WaitlistManagement;
